import {
  Button,
  Card,
  Checkbox,
  Inline,
  Stack,
  Typography,
  useViewport,
} from '@omnipresentgroup/design-system';
import { useMemo, useState } from 'react';

import { ContractorLiabilityShieldDescription } from '../../../contractor-liability-shield';
import type {
  AddContractorFields,
  AddContractorLiabilityShieldFields,
} from '../../types';
import type { StepComponentProps } from '../Stepper';
import { AddContractorLiabilityShieldRequirements } from './AddContractorLiabilityShieldRequirements';
import { MisclassificationRiskCard } from './MisclassificationRiskCard';

export const ACKNOWLEDGE_CHECKBOX_LABEL =
  "I've assessed and verified the contractor's classification and assume full responsibility for any misclassification risks.";

type Props = StepComponentProps<AddContractorFields> & {
  companyId: string;
};

type OmniProtectInterestCardProps = {
  title: string;
  selected: boolean;
  id: string;
};

export const AddContractorLiabilityShield = ({ data, next, back }: Props) => {
  const viewport = useViewport();

  const initialValues = {
    misclassificationRiskAccepted: false,
    contractTemplateUsageAccepted: false,
  };

  const [values, setValues] = useState<AddContractorLiabilityShieldFields>(
    data?.liabilityShield || initialValues,
  );

  const [registerInterestCards, setRegisterInterestCard] = useState<
    OmniProtectInterestCardProps[]
  >([
    {
      title: 'I’m interested to know more about OmniProtect',
      selected: values.isInterestedInOmniprotect === true,
      id: 'interested',
    },
    {
      title: 'I’m not interested',
      selected: values.isInterestedInOmniprotect === false,
      id: 'not-interested',
    },
  ]);

  const isOmniprotectInterestSelected = useMemo(
    () =>
      registerInterestCards.find(
        (card) => card.selected && card.id === 'interested',
      ),
    [registerInterestCards],
  );

  const disableNextButton = useMemo(
    () =>
      (isOmniprotectInterestSelected &&
        !values.contractTemplateUsageAccepted) ||
      registerInterestCards.every((card) => !card.selected),
    [
      isOmniprotectInterestSelected,
      registerInterestCards,
      values.contractTemplateUsageAccepted,
    ],
  );

  return (
    <Stack gap="16">
      <Card maxW={600} p={viewport.isPhone ? '24' : '32'} w="stretch">
        <Stack gap="32">
          <MisclassificationRiskCard />
          <Checkbox
            id="contractor-classification-acknowledge-checkbox"
            checked={values.misclassificationRiskAccepted}
            onCheckedChange={() =>
              setValues({
                ...values,
                misclassificationRiskAccepted:
                  !values.misclassificationRiskAccepted,
              })
            }
            label={ACKNOWLEDGE_CHECKBOX_LABEL}
          />
        </Stack>
      </Card>
      {values.misclassificationRiskAccepted && (
        <Card maxW={600} p={viewport.isPhone ? '24' : '32'} w="stretch">
          <Stack gap="32">
            <ContractorLiabilityShieldDescription />
            <Stack gap="16">
              {registerInterestCards.map((card) => (
                <Card
                  key={card.id}
                  id={card.id}
                  interactive
                  variant="primary"
                  selected={card.selected}
                  p="16"
                  onClick={() => {
                    setValues({
                      ...values,
                      isInterestedInOmniprotect: card.id === 'interested',
                    });
                    setRegisterInterestCard((previous) =>
                      previous.map((c) => ({
                        ...c,
                        selected: c.id === card.id,
                      })),
                    );
                  }}
                >
                  <Typography as="span" size="16" weight="medium">
                    {card.title}
                  </Typography>
                </Card>
              ))}
            </Stack>
            {isOmniprotectInterestSelected && (
              <AddContractorLiabilityShieldRequirements
                values={values}
                setValues={setValues}
              />
            )}
            <Inline justify="space-between">
              <Button
                variant="secondary"
                onClick={() => {
                  back?.handleClick?.({ liabilityShield: values });
                }}
                disabled={back?.disabled}
                loading={back?.loading}
              >
                Back
              </Button>
              <Button
                onClick={() => next?.handleClick?.({ liabilityShield: values })}
                disabled={next?.disabled || disableNextButton}
                loading={next?.loading}
              >
                Next
              </Button>
            </Inline>
          </Stack>
        </Card>
      )}
    </Stack>
  );
};
